import {
  ColDef,
  FilterChangedEvent,
  GridApi,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { useBulkSheetState as useBulkSheetStateBase } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useCallback } from 'react'
import _ from 'lodash'

export const useBulkSheetState = (
  gridApi: GridApi | undefined,
  functionUuid: string,
  uiStateKey: UiStateKey
) => {
  const base = useBulkSheetStateBase(functionUuid, uiStateKey)

  const restoreColumnState = useCallback(() => {
    if (!gridApi || _.isEmpty(base.columnState)) return
    gridApi.applyColumnState({
      state: base.columnState,
      applyOrder: true,
    })
  }, [base.columnState, gridApi])

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [base, gridApi])

  const restoreExpandedRows = useCallback(() => {
    if (_.isEmpty(base.expandedRowIds) || !gridApi) return
    setTimeout(() => {
      base.expandedRowIds.forEach(id => {
        gridApi?.getRowNode(id)?.setExpanded(true)
      })
    }, 200)
  }, [base.expandedRowIds, gridApi])

  const onRowGroupOpended = useCallback(
    (e: RowGroupOpenedEvent) => {
      const key = e.node.key
      if (!key) return
      if (e.expanded) {
        base.expandRows([key])
      } else {
        base.collapseRows([key])
      }
    },
    [base]
  )

  const onFirstDataRendered = useCallback(
    e => {
      restoreExpandedRows()
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridApi?.setFilterModel(base.filterState ?? null)
      }
      base.finishRestoring()
    },
    [base, gridApi, restoreColumnState, restoreExpandedRows]
  )

  const onGridReady = useCallback(() => {
    restoreColumnState()
  }, [restoreColumnState])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filter = e.api.getFilterModel()
      delete filter['uuid']
      base.saveFilterState(filter)
      return Object.keys(filter)
        .map(col => e.api.getColumnDef(col))
        .filter(v => !!v && v.field !== 'uuid') as ColDef[]
    },
    [base]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      const sortedCols = e.api
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => e.api.getColumnDef(col.colId))
        .filter(v => !!v)
      e.api.setGridOption('suppressRowDrag', 0 < sortedCols.length)
      return sortedCols
    },
    [rememberColumnState]
  )

  return {
    restoreColumnState,
    rememberColumnState,
    restoreExpandedRows,
    onRowGroupOpended,
    onFirstDataRendered,
    onGridReady,
    onFilterChanged,
    onSortChanged,
    base,
  }
}
